import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ModalV2,
  SkeletonLoader,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { useHistory, useLocation } from 'react-router-dom';

import {
  AppointmentCancellationEvents,
  EditAppointmentSource,
  PatientEvents,
  PatientScheduleAppointmentEvents,
} from '~/analytics/patientEvents';
import ScheduleView from '~/components/common/calendar/schedule/ScheduleView';
import Scheduler from '~/components/common/calendar/Scheduler';
import ErrorMessage from '~/components/common/ErrorMessage';
import { PAGE_LAYOUT } from '~/lib/constants';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { CalendarListTypeOption } from '~/lib/constants/appointment.constants';

import { GlobalErrorCode, Listeners } from '~/lib/constants/global.constants';
import { PatientTabs } from '~/lib/constants/patients.constants';
import { PatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import useAppointmentBookingEligibility from '~/lib/hooks/useAppointmentBookingEligibility';
import useAppointments from '~/lib/hooks/useAppointments';
import useErrorToast from '~/lib/hooks/useErrorToast';
import {
  getAppointmentFromExternalId,
  getDefaultPatientAppointmentsTabDate,
} from '~/lib/util/appointment.util';
import { getFetchEventsDateFiltersV2 } from '~/lib/util/calendar.util';
import {
  deriveScheduleItems,
  getLastScheduleEventStartDate,
  getNextScheduleEventStartDate,
  getScheduleItemFromExternalId,
  ScheduleType,
} from '~/lib/util/schedule/schedule.util';
import { Path } from '~/routes/constants';
import {
  useAppointmentDispatch,
  useAppointmentSelector,
} from '~/store/reducers/appointment';
import { useGetPatientTreatmentEpisodesQuery } from '~/store/services/clinicalData';
import {
  useGetGroupSectionEventDatesQuery,
  useLazyGetGroupClassEventsQuery,
} from '~/store/services/groupClass';
import {
  useLazyGetPatientAndSupportRegisteredGroupClassCountsQuery,
  useLazyGetPatientAndSupportRegisteredGroupClassCountsV2Query,
} from '~/store/services/patients';
import { useFlags } from 'launchdarkly-react-client-sdk';

import NewAppointmentHeader, {
  CalendarListTypeTitle,
  CalendarScheduleTabProps,
} from './NewAppointmentHeader';

import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';
import { useGetAppointmentsWithNotesQuery } from '~/store/services/appointment';
import { useLazyGetGroupClassNotesByEventExternalIdsQuery } from '~/store/services/notes/notes';
import EventDayCard from '~/components/common/calendar/schedule/day-view/EventDayCard';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';
import CancelAppointmentModal from '~/components/maud/calendar/schedule/modals/CancelAppointmentModal';
import { useIsPatientPath } from '~/lib/hooks/useIsPatientPath';
import { useConsolidatedScheduleItems } from '~/lib/hooks/useConsolidatedScheduleItems';
import { useGroupEventMeetingDetails } from '~/lib/hooks/useGroupEventMeetingDetails';

export interface ScheduleRenderProps {
  currentDate: DateTime;
  height: string;
  lastScheduleDate: string;
  nextScheduleDate: string;
  onCancelAppointment: (externalId: string) => void;
  onCancelledGroupClicked: (startDateTime: string) => void;
  onCloseEventDetailsModal: () => void;
  onNextScheduleDateClicked: (nextScheduleDate: string) => void;
  onNotesClicked: (externalId: string) => void;
  onScheduleItemClicked: (externalId: string) => void;
  onZoomClicked: (externalId: string) => void;
  scheduledItems: ScheduleItemBase[];
}

interface PatientAppointmentsTabProps {
  children?: (props: ScheduleRenderProps) => ReactNode;
  listType?: AppointmentListType;
  onDateChange?: (date: string) => void;
  onViewChange?: (view: AppointmentListType, date?: string) => void;
  patient: Patient;
  patientClinicalProfile?: PatientClinicalProfile;
  patientId: string;
}

const TOP_LEVEL_ID = 'patient-profile-appointments-tab';

const PatientAppointmentsTab = ({
  children,
  listType: listTypeProp,
  onDateChange,
  onViewChange,
  patient,
  patientId,
  patientClinicalProfile,
}: PatientAppointmentsTabProps) => {
  const {
    currentUser: { userId },
    addListener,
    removeListener,
  } = useGlobalContext();
  const history = useHistory();
  const location = useLocation();
  const { isOnPatientProfile } = useGlobalContext();

  const {
    groupsRestructuring: isGroupsRestructuringEnabled,
    viewCancelledAppointmentsInMaud: isViewCancelledAppointmentsInMaudEnabled,
  } = useFlags();
  const { openGroupEventMeeting } = useGroupEventMeetingDetails();

  const [isSchedulerOpen, setIsSchedulerOpen] = useState<boolean>(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [schedulerKey, setSchedulerKey] = useState<number>(0);
  const [schedulerError, setSchedulerError] = useState<{
    id: string;
    message: string;
  }>(null);

  const [
    isConfirmScheduleAppointmentModalOpen,
    setIsConfirmScheduleAppointmentModalOpen,
  ] = useState<boolean>(false);

  const [isGroupClassReloadNeeded, setIsGroupClassReloadNeeded] =
    useState<boolean>(false);
  const [listType, setListType] = useState<AppointmentListType>(
    listTypeProp ?? CalendarListTypeOption.DAILY_VIEW,
  );

  useEffect(() => {
    if (listTypeProp !== undefined && listTypeProp !== listType) {
      setListType(listTypeProp);
    }
  }, [listTypeProp]);
  const [selectedScheduledItem, setSelectedScheduledItem] =
    useState<ScheduleItemBase>(null);
  const [isOpenAppointCancel, setIsOpenAppointCancel] =
    useState<boolean>(false);
  const [showCancelledAppointments, setShowCancelledAppointments] =
    useState<boolean>(false);

  const { appointmentCurrentDate: currentDate } = useAppointmentSelector();

  const { startDate, endDate } = useMemo(() => {
    const currentDateDT = DateTime.fromISO(currentDate);
    return getFetchEventsDateFiltersV2(listType, currentDateDT);
  }, [currentDate, listType]);

  const [getGroupsCounts, { data: groupCount }] =
    useLazyGetPatientAndSupportRegisteredGroupClassCountsQuery();

  const [getGroupsCountsV2, { data: groupCountV2 }] =
    useLazyGetPatientAndSupportRegisteredGroupClassCountsV2Query();

  const registeredGroupCount = isGroupsRestructuringEnabled
    ? groupCountV2
    : groupCount?.data;

  const [
    getGroupClassNotesByEventExternalIds,
    { isFetching: gcNotesDataLoading },
  ] = useLazyGetGroupClassNotesByEventExternalIdsQuery();

  const [
    getPatientGroupClassEvents,
    {
      data: patientGroupClassEvents,
      isLoading: isGetPatientGroupClassEventsLoading,
    },
  ] = useLazyGetGroupClassEventsQuery();

  const { openNoteDrawer } = useEditNoteDrawerContext();

  const { setAppointmentCurrentDate: setCurrentDate } =
    useAppointmentDispatch();

  const { trackEvent } = useAnalytics();

  const hasInitializedDate = useRef(false);

  useEffect(() => {
    if (isGroupsRestructuringEnabled) {
      getGroupsCountsV2(patientId);
    } else {
      getGroupsCounts(patientId);
    }
    if (!hasInitializedDate.current) {
      hasInitializedDate.current = true;
      setCurrentDate(
        DateTime.fromJSDate(
          getDefaultPatientAppointmentsTabDate(location.search),
        ).toString(),
      );
    } else {
      const urlDate = new URLSearchParams(location.search).get('date');
      if (urlDate) {
        const parsedDT = DateTime.fromJSDate(
          getDefaultPatientAppointmentsTabDate(location.search),
        );
        const currentDT = DateTime.fromISO(currentDate);
        if (!parsedDT.hasSame(currentDT, 'day')) {
          setCurrentDate(parsedDT.toString());
        }
      }
    }
  }, [location]);

  useEffect(() => {
    onDateChange?.(currentDate);
  }, [currentDate]);

  const tabs: Record<CalendarListTypeOption, CalendarScheduleTabProps> = {
    [CalendarListTypeOption.DAILY_VIEW]: {
      listType: CalendarListTypeOption.DAILY_VIEW,
      title: CalendarListTypeTitle.DAILY_VIEW,
    },
    [CalendarListTypeOption.WEEKLY_VIEW]: {
      listType: CalendarListTypeOption.WEEKLY_VIEW,
      title: CalendarListTypeTitle.WEEKLY_VIEW,
    },
    [CalendarListTypeOption.GROUPS]: {
      count: registeredGroupCount || 0,
      listType: CalendarListTypeOption.GROUPS,
      title: CalendarListTypeTitle.GROUPS,
    },
  };

  const isPatientPath = useIsPatientPath();

  const {
    getProviderTeamCareTeam,
    getZoomUrl,
    hasSchedulingAuthorization,
    patientCareTeams,
  } = useAppointments();

  const { data: treatmentEpisodesData } = useGetPatientTreatmentEpisodesQuery(
    patient?.userId,
  );

  const { data: appointmentsNotesData, isFetching: appointmentsLoading } =
    useGetAppointmentsWithNotesQuery({
      endDate,
      filterByKey: 'patient-external-id',
      filterByValue: patientId,
      includeTimezoneDetails: true,
      shouldIncludeCancelled: showCancelledAppointments,
      startDate,
    });

  const appointmentDetails =
    appointmentsNotesData?.appointmentDetails as GetAppointmentDetailsResponse;

  const { data: groupSectionEventDatesData } =
    useGetGroupSectionEventDatesQuery(
      {
        patientExternalId: patientId,
        startDate,
        endDate,
      },
      {
        skip: !isGroupsRestructuringEnabled || !patientId,
      },
    );

  const nextScheduleDate = useMemo<string>(() => {
    const nextEventStartDate = isGroupsRestructuringEnabled
      ? groupSectionEventDatesData?.nextEventStartDate
      : patientGroupClassEvents?.nextEventStartDate;
    return getNextScheduleEventStartDate(
      appointmentDetails?.nextAppointmentStartDate,
      nextEventStartDate,
    );
  }, [
    appointmentDetails?.nextAppointmentStartDate,
    isGroupsRestructuringEnabled,
    groupSectionEventDatesData?.nextEventStartDate,
    patientGroupClassEvents?.nextEventStartDate,
  ]);

  const lastScheduleDate = useMemo<string>(() => {
    const lastEventStartDate = isGroupsRestructuringEnabled
      ? groupSectionEventDatesData?.lastEventStartDate
      : patientGroupClassEvents?.lastEventStartDate;
    return getLastScheduleEventStartDate(
      appointmentDetails?.lastAppointmentStartDate,
      lastEventStartDate,
    );
  }, [
    appointmentDetails?.lastAppointmentStartDate,
    isGroupsRestructuringEnabled,
    groupSectionEventDatesData?.lastEventStartDate,
    patientGroupClassEvents?.lastEventStartDate,
  ]);

  const { showErrorToast } = useErrorToast();

  const { isBookingEnabled } = useAppointmentBookingEligibility({
    patient,
    treatmentEpisodes: treatmentEpisodesData?.data,
  });

  const fetchNotesForGroupClassEvents = (
    groupClassEventsData: GroupClassEventsBase[],
  ): void => {
    const groupClassEventExternalIds = groupClassEventsData.map(
      (event: GroupClassEventsBase) => event.groupClassEventExternalId,
    );
    getGroupClassNotesByEventExternalIds(groupClassEventExternalIds);
  };

  // Goal is to eventually remove this when all our network logic is in rtk query
  const getGroupClassEventsAndTheirNotes = (): void => {
    if (!isGroupsRestructuringEnabled) {
      getPatientGroupClassEvents({
        endDateTime: endDate,
        patientExternalId: patientId,
        startDateTime: startDate,
      }).then((response) => {
        if (response.data?.data?.length > 0 && isPatientPath) {
          fetchNotesForGroupClassEvents(response.data.data);
        }
      });
    }
  };

  useEffect(getGroupClassEventsAndTheirNotes, [
    currentDate,
    listType,
    isGroupsRestructuringEnabled,
  ]);

  useEffect(() => {
    if (patientId) {
      getProviderTeamCareTeam(patientId);
    }
  }, [patientId]);

  const scheduleItems = useMemo<ScheduleItemBase[]>(() => {
    return deriveScheduleItems(
      userId,
      appointmentDetails?.data,
      patientGroupClassEvents?.data,
      appointmentsNotesData?.notes ?? [],
      isViewCancelledAppointmentsInMaudEnabled,
    );
  }, [
    appointmentDetails?.data,
    patientGroupClassEvents?.data,
    userId,
    appointmentsNotesData?.notes,
    isViewCancelledAppointmentsInMaudEnabled,
  ]);

  const { consolidatedScheduleItems, isLoading } = useConsolidatedScheduleItems(
    {
      scheduleItems,
      patientId: patientId,
      filterByValue: userId,
      startDateTime: startDate,
      endDateTime: endDate,
      pageSize: 200,
    },
  );

  const handleTabChange = (view: AppointmentListType, date?: string) => {
    setListType(view);
    onViewChange?.(view, date);
  };

  const handleCancelledGroupClick = (startDateTime: string) => {
    const clickedDate = DateTime.fromISO(startDateTime).startOf('day');
    setCurrentDate(clickedDate.toString());
    handleTabChange(CalendarListTypeOption.DAILY_VIEW, clickedDate.toString());
  };

  const onDateChanged = (moveBy: number) => {
    const isDailyView = listType === CalendarListTypeOption.DAILY_VIEW;
    const duration = isDailyView ? { days: moveBy } : { weeks: moveBy };

    setCurrentDate(DateTime.fromISO(currentDate).plus(duration).toString());
  };

  const handleScheduleAppointmentClick = (): void => {
    trackEvent(
      PatientEvents.scheduleAppointmentPatientCalendarTabButtonClicked,
      {
        Source: AnalyticsSourceValues.PATIENT_CALENDAR_TAB,
      },
    );
    setIsSchedulerOpen(true);
    trackEvent(
      PatientScheduleAppointmentEvents.scheduleAppointmentModalAppointmentSelectionView,
      {
        Source: AnalyticsSourceValues.PATIENT_CALENDAR_TAB,
      },
    );
  };

  const onSchedulerClose = (date?: string): void => {
    if (date) {
      setCurrentDate(date);
    }
    setIsSchedulerOpen(false);
    setSchedulerKey((key) => key + 1);
    setIsConfirmScheduleAppointmentModalOpen(false);
  };

  const onSchedulerError = (id: string, message: string): void => {
    onSchedulerClose();
    setSchedulerError({ id, message });
  };

  const handleCloseAndResetCancelModal = (): void => {
    setIsOpenAppointCancel(false);
  };

  const handleOpenZoomClick = (externalId: string, source: string): void => {
    let eventToTrack;
    switch (source) {
      case AnalyticsSourceValues.PATIENT_CALENDAR_TAB:
        eventToTrack = PatientEvents.openZoomPatientCalendarTabButtonClicked;
        break;
      case AnalyticsSourceValues.PATIENT_CALENDAR_TAB_MODAL:
        eventToTrack =
          PatientEvents.openZoomPatientCalendarTabModalButtonClicked;
        break;
    }
    trackEvent(eventToTrack, {
      Source: AnalyticsSourceValues.PATIENT_CALENDAR_TAB,
    });
    const scheduledItem = getScheduleItemFromExternalId(
      consolidatedScheduleItems,
      externalId,
    );
    if (scheduledItem) {
      if (scheduledItem.typeOf === ScheduleType.APPOINTMENT) {
        getZoomUrl(
          externalId,
          (zoomUrl) => {
            window.open(zoomUrl, '_blank', 'noopener');
          },
          (error: string, statusCode: number) => {
            showErrorToast(
              GlobalErrorCode.GET_GROUP_CLASS_EVENT_MEETING_DETAILS,
              statusCode,
              error,
            );
          },
        );
      } else {
        openGroupEventMeeting(externalId);
      }
    }
  };

  const handleNotesClicked = (externalId: string) => {
    if (isDetailsOpen) setIsDetailsOpen(false);
    const scheduledItem = getScheduleItemFromExternalId(
      consolidatedScheduleItems,
      externalId,
    );

    if (scheduledItem) {
      const noteData = {
        appointmentDetails: getAppointmentFromExternalId(
          appointmentDetails?.data,
          externalId,
        ),
        existingNoteId: null,
      };

      if (scheduledItem?.isNoteInSignedState) {
        // Navigating to Patient Notes
        history.push({
          pathname: `${Path.MAUD_PATIENTS}/${patientId}/${
            PatientTabsCollection[PatientTabs.NOTES].subPath
          }`,
          state: {
            appointment: noteData.appointmentDetails,
          },
        });
      } else {
        const note = (appointmentsNotesData?.notes ?? [])?.find(
          (note) => note.appointmentExternalId === externalId,
        );
        const apptDetails = appointmentDetails.data.find(
          (a) => a.appointmentExternalId === externalId,
        );
        if (scheduledItem?.isNoteInDraftState) {
          openNoteDrawer(patientId, note?.noteExternalId, apptDetails);

          // Add listener to receive signal when note is saved
          addListener(Listeners.APPOINTMENTS_NOTE_REFRESH, () => {
            removeListener(Listeners.APPOINTMENTS_NOTE_REFRESH);
            getGroupClassEventsAndTheirNotes();
          });
        } else {
          // not in signed or draft state, create a new note
          openNoteDrawer(patientId, undefined, apptDetails);
        }
      }
    }
  };

  const handleScheduleItemClicked = (externalId: string) => {
    setSelectedScheduledItem(
      getScheduleItemFromExternalId(consolidatedScheduleItems, externalId),
    );
    setIsDetailsOpen(true);
  };

  const isOverviewLoading =
    isGetPatientGroupClassEventsLoading ||
    appointmentsLoading ||
    gcNotesDataLoading;

  const [isAnyAppointmentTypeSelected, setIsAnyAppointmentTypeSelected] =
    useState<boolean>(false);

  const handleSchedulerModalClose = (
    appointmentCurrentDate: string,
    shouldShowWarningModal: boolean,
  ) => {
    if (isAnyAppointmentTypeSelected) {
      if (shouldShowWarningModal !== false) {
        setIsConfirmScheduleAppointmentModalOpen(true);
      } else {
        onSchedulerClose(appointmentCurrentDate ?? currentDate);
      }
    } else {
      onSchedulerClose(appointmentCurrentDate ?? currentDate);
    }
  };
  const isAuthorisedToScheduleAppointment =
    isBookingEnabled && hasSchedulingAuthorization && isOnPatientProfile;

  const handleNextDateClick = (date: string) => {
    setCurrentDate(DateTime.fromISO(date).toString());
  };

  const handleAppointmentCancelClick = (externalId: string) => {
    const selectedItem = getScheduleItemFromExternalId(
      consolidatedScheduleItems,
      externalId,
    );

    setSelectedScheduledItem(selectedItem);
    trackEvent(
      AppointmentCancellationEvents.cancelAppointmentKebabMenuClicked,
      {
        appointmentId: selectedItem?.externalId,
        appointmentTitle: selectedItem?.title,
        isRecurringEvent: selectedItem?.isRecurring,
        Source: EditAppointmentSource.providerCalendarTab,
      },
    );
    setIsDetailsOpen(false);
    setIsOpenAppointCancel(true);
  };

  const handleCancelAppointmentModalClick = () => {
    trackEvent(
      AppointmentCancellationEvents.cancelAppointmentKebabMenuClicked,
      {
        appointmentId: selectedScheduledItem?.externalId,
        appointmentTitle: selectedScheduledItem?.title,
        isRecurringEvent: selectedScheduledItem?.isRecurring,
        Source: EditAppointmentSource.providerCalendarTab,
      },
    );
    setIsDetailsOpen(false);
    setIsOpenAppointCancel(true);
  };

  return (
    <ChakraFlex direction="column">
      {schedulerError && (
        <ErrorMessage id={`${TOP_LEVEL_ID}__${schedulerError.id}`}>
          {schedulerError.message}
        </ErrorMessage>
      )}

      <NewAppointmentHeader
        activeTab={listType}
        currentDate={DateTime.fromISO(currentDate)}
        handleScheduleAppointmentClick={() => {
          handleScheduleAppointmentClick();
        }}
        onDateChange={onDateChanged}
        onNewRegistration={() => setIsGroupClassReloadNeeded(true)}
        showCancelledAppointments={showCancelledAppointments}
        setShowCancelledAppointments={setShowCancelledAppointments}
        onTabChange={handleTabChange}
        patient={patient}
        shouldEnableBooking={isAuthorisedToScheduleAppointment}
        tabs={Object.values(tabs)}
      />

      <ChakraBox
        height="calc(100vh - 243px)"
        overflow="auto"
        paddingRight={PAGE_LAYOUT.rightSpacing}
      >
        <SkeletonLoader
          id={TOP_LEVEL_ID}
          isLoaded={!isOverviewLoading && !isLoading}
        >
          {children ? (
            children({
              currentDate: DateTime.fromISO(currentDate),
              height: 'calc(100vh - 243px)',
              lastScheduleDate,
              nextScheduleDate,
              onCancelAppointment: handleAppointmentCancelClick,
              onCancelledGroupClicked: handleCancelledGroupClick,
              onCloseEventDetailsModal: () => setSelectedScheduledItem(null),
              onNextScheduleDateClicked: handleNextDateClick,
              onNotesClicked: handleNotesClicked,
              onScheduleItemClicked: handleScheduleItemClicked,
              onZoomClicked: (externalId) => {
                handleOpenZoomClick(
                  externalId,
                  AnalyticsSourceValues.PATIENT_CALENDAR_TAB,
                );
              },
              scheduledItems: consolidatedScheduleItems,
            })
          ) : (
            <ScheduleView
              currentDate={DateTime.fromISO(currentDate)}
              height="calc(100vh - 243px)"
              lastScheduleDate={lastScheduleDate}
              mode={listType}
              needReload={isGroupClassReloadNeeded}
              nextScheduleDate={nextScheduleDate}
              onCancelAppointment={handleAppointmentCancelClick}
              onCancelledGroupClicked={handleCancelledGroupClick}
              onCloseEventDetailsModal={() => setSelectedScheduledItem(null)}
              onFetchGroupClassEvents={() =>
                getPatientGroupClassEvents({
                  patientExternalId: patientId,
                  startDateTime: startDate,
                  endDateTime: endDate,
                })
              }
              onNextScheduleDateClicked={handleNextDateClick}
              onNotesClicked={handleNotesClicked}
              onScheduleItemClicked={handleScheduleItemClicked}
              onZoomClicked={(externalId) => {
                handleOpenZoomClick(
                  externalId,
                  AnalyticsSourceValues.PATIENT_CALENDAR_TAB,
                );
              }}
              patientId={patientId}
              resetNeedReload={() => setIsGroupClassReloadNeeded(false)}
              scheduledItems={consolidatedScheduleItems}
            />
          )}
        </SkeletonLoader>
      </ChakraBox>

      <ModalV2
        id={`${TOP_LEVEL_ID}__event-details`}
        isOpen={['Group Class Event', 'Group Class'].includes(
          selectedScheduledItem?.typeOf,
        )}
        onClose={() => setSelectedScheduledItem(null)}
        size="5xl"
        title="Event Details"
      >
        <EventDayCard
          key={selectedScheduledItem?.externalId}
          onCancelAppointment={() => {
            setIsDetailsOpen(false);
            setIsOpenAppointCancel(true);
          }}
          onNextScheduleDateClicked={handleNextDateClick}
          onNoteButtonClick={handleNotesClicked}
          onZoomClick={(externalId: string) =>
            handleOpenZoomClick(
              externalId,
              AnalyticsSourceValues.PATIENT_CALENDAR_TAB_MODAL,
            )
          }
          scheduleItem={selectedScheduledItem}
          shouldCloseModal={() => setIsDetailsOpen(false)}
        />
      </ModalV2>

      {selectedScheduledItem?.typeOf === ScheduleType.APPOINTMENT && (
        <ModalV2
          id={`${TOP_LEVEL_ID}__appointment-details`}
          isOpen={isDetailsOpen}
          onClose={() => setSelectedScheduledItem(null)}
          size="5xl"
          title="Appointment Details"
        >
          <EventDayCard
            key={selectedScheduledItem.externalId}
            onCancelAppointment={handleCancelAppointmentModalClick}
            onNextScheduleDateClicked={handleNextDateClick}
            onNoteButtonClick={handleNotesClicked}
            onZoomClick={(externalId: string) =>
              handleOpenZoomClick(
                externalId,
                AnalyticsSourceValues.PATIENT_CALENDAR_TAB_MODAL,
              )
            }
            scheduleItem={selectedScheduledItem}
            shouldCloseModal={() => setIsDetailsOpen(false)}
          />
        </ModalV2>
      )}

      {isOpenAppointCancel && (
        <CancelAppointmentModal
          appointmentExternalId={selectedScheduledItem?.externalId}
          appointmentTitle={selectedScheduledItem?.title}
          isOpen={isOpenAppointCancel}
          isRecurring={selectedScheduledItem?.isRecurring}
          onClose={handleCloseAndResetCancelModal}
        />
      )}
      <Scheduler
        careTeamMembers={patientCareTeams}
        id={TOP_LEVEL_ID}
        isOpen={isSchedulerOpen}
        key={schedulerKey}
        onAppointmentTypeSelected={() => setIsAnyAppointmentTypeSelected(true)}
        onClose={handleSchedulerModalClose}
        onError={onSchedulerError}
        patient={patient}
        programName={patientClinicalProfile?.programName}
        showLoadingSpinner
      />

      <DiscardChangesModalV2
        isOpen={isConfirmScheduleAppointmentModalOpen}
        onClose={() => setIsConfirmScheduleAppointmentModalOpen(false)}
        onConfirm={() => {
          onSchedulerClose(currentDate);
          setIsAnyAppointmentTypeSelected(false);
        }}
      />
    </ChakraFlex>
  );
};

export default PatientAppointmentsTab;
